import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { KineticText, Reveal } from "@/components/motion/primitives";
import { ArrowRight, Loader2 } from "lucide-react";
import { SectionNav, BackToTop } from "@/components/ChapterNav";
import Seo from "@/components/Seo";

const sectionId = (k) => `testament-${k.toLowerCase()}`;

function Testament({ id, label, books, tone, testId }) {
  const max = Math.max(...books.map((b) => b.count), 1);
  return (
    <section id={sectionId(id)} data-section={id} className="mt-10 scroll-mt-36" data-testid={testId}>
      <div className="mb-5 flex items-center gap-3">
        <h2 className={`font-mono text-xs uppercase tracking-[0.25em] ${tone}`}>{label}</h2>
        <span className="h-px flex-1 bg-border" />
        <span className="font-mono text-xs text-muted-foreground">{books.length} books · {books.reduce((n, b) => n + b.count, 0)} questions</span>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {books.map((b, i) => (
          <Reveal key={b.name} delay={Math.min(i, 10) * 0.03}>
            <Link
              to={`/book/${encodeURIComponent(b.name)}`}
              data-testid={`books-index-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative flex h-full flex-col justify-between overflow-hidden rounded-xl border border-border bg-card p-4 transition-colors hover:border-gold"
            >
              <span className="absolute inset-x-0 bottom-0 h-1 bg-gold/20">
                <span className="block h-full bg-gold/70 transition-all" style={{ width: `${Math.max(6, (b.count / max) * 100)}%` }} />
              </span>
              <span className="font-mono text-[10px] text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
              <span className="mt-3 font-serif text-lg font-semibold leading-tight text-foreground">{b.name}</span>
              <span className="mt-2 mb-1 flex items-center justify-between font-mono text-xs text-muted-foreground">
                {b.count} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1 group-hover:text-gold" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function ExploreBooks() {
  const { data: meta, isLoading } = useQuery({
    queryKey: ["meta"],
    queryFn: async () => (await api.get("/questions/meta")).data,
  });
  const ot = (meta?.books || []).filter((b) => b.testament === "OT");
  const nt = (meta?.books || []).filter((b) => b.testament === "NT");

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="explore-books">
      <Seo title="Explore by Book" description="Every book of the Bible that records a question, Genesis to Revelation, with counts." />
      <header className="mb-8">
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Genesis to Revelation</span>
        <KineticText as="h1" text="Explore by book" className="mt-2 block font-serif text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-5xl" testId="books-headline" />
        <Reveal delay={0.3}>
          <p className="mt-3 max-w-2xl text-muted-foreground">Every book that records a question, in Bible order. The gold bar shows how many questions each one holds.</p>
        </Reveal>
      </header>
      {isLoading ? (
        <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <>
          <SectionNav
            items={[{ key: "OT", label: "Old Testament", count: ot.reduce((n, b) => n + b.count, 0) }, { key: "NT", label: "New Testament", count: nt.reduce((n, b) => n + b.count, 0) }]}
            idFor={sectionId}
            label="Jump to"
            testId="testament-nav"
          />
          <Testament id="OT" label="Old Testament" books={ot} tone="text-primary" testId="books-ot" />
          <Testament id="NT" label="New Testament" books={nt} tone="text-secondary dark:text-sky-300" testId="books-nt" />
          <BackToTop />
        </>
      )}
    </div>
  );
}
